import {
  FhirHTTPCodes,
  FhirStatus,
  StatusAlert,
  StatusCode,
  StatusIcon,
  Title,
} from "@fyrstain/fhir-front-library";
import i18n from "i18next";
import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { Card, Form, Table } from "react-bootstrap";
import ReactJson from "react-json-view";
import { useNavigate, useParams } from "react-router-dom";
import PandoraPage from "../../components/PandoraPage/PandoraPage";
import styles from "./auditEventExchangeDetails.module.css";
import { toPublicUrl } from "src/services/PublicUrl";
import { getAuditEventById, changeServer } from "src/services/AuditEventDataService";
import { useEnvironment } from "src/context/EnvironmentContext";

const AuditEventExchangeDetails: FunctionComponent = () => {
  /////////////////////////////////////
  //             State               //
  /////////////////////////////////////

  const [loading, setLoading] = useState<boolean>(false);
  const { exchangeId } = useParams<{ exchangeId: string }>();

  const [method, setMethod] = useState("");
  const [path, setPath] = useState("");
  const [sender, setSender] = useState("");
  const [statusResponse, setStatusResponse] = useState("");
  const [partHeaderRequest, setPartHeaderRequest] = useState([] as any[]);
  const [partHeaderResponse, setPartHeaderResponse] = useState([] as any[]);
  const [bodyRequest, setBodyRequest] = useState("N/A");
  const [bodyResponse, setBodyResponse] = useState("N/A");
  const [recorded, setRecorded] = useState("");
  const [validationReportLink, setValidationReportLink] = useState("");

  const { currentEnvironment } = useEnvironment();

  /////////////////////////////////////
  //             Actions             //
  /////////////////////////////////////

  const navigate = useNavigate();

  useEffect(() => {
    changeServer(currentEnvironment?.messageUrl ?? process.env.REACT_APP_FHIR_URL_AUDIT ?? "fhir");
    loadPage();
  }, [exchangeId]);

  const onError = useCallback(() => {
    navigate("/Error");
  }, [navigate]);

  /**
   * Load exchange details from the back to populate the table.
   */
  async function loadExchangeDetails() {
    try {
      const auditEvent = await getAuditEventById(exchangeId ?? "");

      if (!auditEvent) {
        onError();
        return;
      }

      setMethod(getMethod(auditEvent));
      setPath(getPath(auditEvent));
      setSender(getSender(auditEvent));
      setStatusResponse(getStatus(auditEvent));
      setPartHeaderRequest(getPartHeaderRequest(auditEvent));
      setPartHeaderResponse(getPartHeaderResponse(auditEvent));
      setBodyRequest(getBodyRequest(auditEvent));
      setBodyResponse(getBodyResponse(auditEvent));
      setRecorded(getRecorded(auditEvent));
      setValidationReportLink(getValidationReportLink(auditEvent));
    } catch (error) {
      onError();
    }
  }

  async function loadPage() {
    setLoading(true);
    await loadExchangeDetails();
    setLoading(false);
  }

  /////////////////////////////////////
  //            Mapping              //
  /////////////////////////////////////

  function getMethod(auditEvent: any): string {
    for (const entity of auditEvent?.entity ?? []){
      for (const detail of entity?.detail ?? []){
        const label = 
          detail?.type?.text ??
          detail?.type?.coding?.[0]?.display ??
          detail?.type?.coding?.[0]?.code;

        if (
          label &&
          ["method", "http method", "request method", "verb"].includes(
            String(label).toLowerCase(),
          )
        ){
          return detail?.valueString ??
            detail?.valueCodeableConcept?.text ??
            detail?.valueCodeableConcept?.coding?.[0]?.display ??
            detail?.valueCodeableConcept?.coding?.[0]?.code ??
            auditEvent?.action ??
            "N/A";
        }
      }
    }
    if (auditEvent?.action === "C") return "POST";
    if (auditEvent?.action === "R") return "GET";
    if (auditEvent?.action === "U") return "PUT";
    if (auditEvent?.action === "D") return "DELETE";
    if (auditEvent?.action === "E") return "POST";
    
    return auditEvent?.action ? auditEvent.action : "N/A";
  }

  function getPath(auditEvent: any): string {
    for (const entity of auditEvent?.entity ?? []) {
      for (const detail of entity?.detail ?? []) {
        const label =
          detail?.type?.text ??
          detail?.type?.coding?.[0]?.display ??
          detail?.type?.coding?.[0]?.code;

        if (label && String(label).toLowerCase() === "route") {
          return (
            detail?.valueString ??
            detail?.valueCodeableConcept?.text ??
            detail?.valueCodeableConcept?.coding?.[0]?.display ??
            detail?.valueCodeableConcept?.coding?.[0]?.code ??
            "N/A"
          );
        }
      }
    if (entity?.query) {
      try {
        const decodedQuery = atob(entity.query);
        if (decodedQuery.startsWith("/")) {
          return decodedQuery;
        }
        try {
          const url = new URL(decodedQuery, "http://dummy");
          return url.pathname || "N/A";
        } catch {
          return decodedQuery;
        }
      } catch {
        return entity.query;
      }
    }
  }
  const queryEntity = auditEvent?.entity?.find(
      (e: any) =>
        e?.what?.display === "query" ||
        e?.description === "query" ||
        e?.query,
    );

    return queryEntity?.description
      ? queryEntity.description
      : queryEntity?.query
        ? queryEntity.query
        : queryEntity?.what?.reference
          ? queryEntity.what.reference
          : queryEntity?.what?.display
            ? queryEntity.what.display
            : auditEvent?.entity?.[0]?.description
              ? auditEvent.entity[0].description
              : auditEvent?.entity?.[0]?.what?.reference
                ? auditEvent.entity[0].what.reference
                : auditEvent?.entity?.[0]?.what?.display
                  ? auditEvent.entity[0].what.display
                  : "N/A";
  }

  function getSender(auditEvent: any): string {
    const requestorAgent = auditEvent?.agent?.find((a: any) => a.requestor);

    return requestorAgent?.who?.display
      ? requestorAgent.who.display
      : requestorAgent?.who?.reference
        ? requestorAgent.who.reference
        : auditEvent?.agent?.[0]?.who?.display
          ? auditEvent.agent[0].who.display
          : auditEvent?.agent?.[0]?.who?.reference
            ? auditEvent.agent[0].who.reference
            : auditEvent?.agent?.[0]?.who?.display
              ? auditEvent.agent[0].who.display
              : auditEvent?.agent?.[0]?.who?.reference
                ? auditEvent.agent[0].who.reference
                : "N/A";
  }

  function getStatus(auditEvent: any): string {
    const code =
      auditEvent?.outcome?.code?.code ??
      auditEvent?.outcome?.code?.display ??
      auditEvent?.outcome?.code;

    switch (code) {
      case "0":
      case "Success":
        return "success"; 
      case "4":
      case "Minor failure":
        return "minorFailure";
      case "8":
      case "Serious failure":
        return "seriousFailure";
      case "12":
      case "Major failure":
        return "majorFailure";
      default:
        return "N/A";
    }
  }

  function getRecorded(auditEvent: any): string {
    return auditEvent?.recorded ? auditEvent.recorded : "N/A";
  }

  function getPartHeaderRequest(auditEvent: any) {
    const result: any[] = [];

    auditEvent?.entity?.forEach((e: any) => {
      if (
        e?.what?.display === "requestHeader" ||
        e?.description === "requestHeader"
      ) {
        e?.detail?.forEach((part: any) => {
          result.push({
            name:
              part?.type?.text ??
              part?.type?.coding?.[0]?.display ??
              part?.type?.coding?.[0]?.code ??
              "N/A",
            valueString:
              part?.valueString ??
              part?.valueCodeableConcept?.text ??
              part?.valueCodeableConcept?.coding?.[0]?.display ??
              part?.valueCodeableConcept?.coding?.[0]?.code ??
              "N/A",
          });
        });
      }
    });

    return result;
  }

  function getPartHeaderResponse(auditEvent: any) {
    const result: any[] = [];

    auditEvent?.entity?.forEach((e: any) => {
      if (
        e?.what?.display === "responseHeader" ||
        e?.description === "responseHeader"
      ) {
        e?.detail?.forEach((part: any) => {
          result.push({
            name:
              part?.type?.text ??
              part?.type?.coding?.[0]?.display ??
              part?.type?.coding?.[0]?.code ??
              "N/A",
            valueString:
              part?.valueString ??
              part?.valueCodeableConcept?.text ??
              part?.valueCodeableConcept?.coding?.[0]?.display ??
              part?.valueCodeableConcept?.coding?.[0]?.code ??
              "N/A",
          });
        });
      }
    });

    return result;
  }

  function getBodyRequest(auditEvent: any): string {
    const bodyRequestEntity = auditEvent?.entity?.find(
      (e: any) => 
        e?.what?.display === "requestBody" ||
        e?.description === "requestBody",
    );

    return bodyRequestEntity?.detail?.[0]?.valueString
      ? bodyRequestEntity.detail[0].valueString
      : bodyRequestEntity?.detail?.[0]?.valueCodeableConcept?.text
        ? bodyRequestEntity.detail[0].valueCodeableConcept.text
        : bodyRequestEntity?.detail?.[0]?.valueCodeableConcept?.coding?.[0]
            ?.display
          ? bodyRequestEntity.detail[0].valueCodeableConcept.coding[0].display
          : "N/A";
  }

  function getBodyResponse(auditEvent: any): string {
    const bodyResponseEntity = auditEvent?.entity?.find(
      (e: any) => 
        e?.what?.display === "responseBody" ||
        e?.description === "responseBody",
    );

    return bodyResponseEntity?.detail?.[0]?.valueString
      ? bodyResponseEntity.detail[0].valueString
      : bodyResponseEntity?.detail?.[0]?.valueCodeableConcept?.text
        ? bodyResponseEntity.detail[0].valueCodeableConcept.text
        : bodyResponseEntity?.detail?.[0]?.valueCodeableConcept?.coding?.[0]
            ?.display
          ? bodyResponseEntity.detail[0].valueCodeableConcept.coding[0].display
          : "N/A";
  }

  function getValidationReportLink(auditEvent: any): string {
    const validationReportEntity = auditEvent?.entity?.find(
      (e: any) =>
        e?.what?.display === "validationReport" ||
        e?.description === "validationReport" ||
        e?.what?.reference?.startsWith("ValidationReport/"),
    );

    return validationReportEntity?.what?.reference
      ? `/ValidationReports/${validationReportEntity.what.reference.split("/").pop()}`
      : "";
  }

  function parseJson(response: string) {
    try {
      return JSON.parse(response);
    } catch (ex) {
      return {};
    }
  }

  /////////////////////////////////////
  //          Page Content           //
  /////////////////////////////////////

  return (
    <PandoraPage titleKey="Message" loading={loading} needsLogin={true}>
      <>
        <div className="section">
          <Card className={styles.card}>
            <Card.Header>
              <Title level={2} content={"Informations"} />
            </Card.Header>
            <Card.Body className="cardBody">
              <div>
                <div className="spaceBetweenContainer">
                  <div className="mediumContainer">
                    <div>
                      <Form.Label className={styles.label}>
                        <strong>ID :</strong>
                      </Form.Label>
                      <Form.Text>{exchangeId}</Form.Text>
                    </div>
                    <div>
                      <Form.Label className={styles.label}>
                        <strong>Action :</strong>
                      </Form.Label>
                      <Form.Text>{method}</Form.Text>
                    </div>
                    <div>
                      <Form.Label className={styles.label}>
                        <strong>Date :</strong>
                      </Form.Label>
                      <Form.Text>{recorded}</Form.Text>
                    </div>
                  </div>

                  <div className="mediumContainer">
                    <div>
                      <Form.Label className={styles.label}>
                        <strong>Expéditeur :</strong>
                      </Form.Label>
                      <Form.Text>{sender}</Form.Text>
                    </div>
                    <div>
                      <Form.Label className={styles.label}>
                        <strong>Requête :</strong>
                      </Form.Label>
                      <Form.Text>{path}</Form.Text>
                    </div>
                  </div>
                </div>

                {validationReportLink && (
                  <div>
                    <Form.Label className={styles.label}>
                      <strong>Validation Report :</strong>
                    </Form.Label>
                    <Form.Text>
                      <a href={toPublicUrl(validationReportLink)}>Voir le rapport</a>
                    </Form.Text>
                  </div>
                )}
              </div>

              <div>
                <StatusAlert
                  status={FhirStatus[statusResponse as keyof typeof FhirStatus]}
                >
                  <div>
                    <div>
                      <StatusIcon
                        status={
                          FhirStatus[statusResponse as keyof typeof FhirStatus]
                        }
                      />
                      <StatusCode
                        status={statusResponse}
                        statusCode={
                          FhirHTTPCodes[
                            statusResponse as keyof typeof FhirHTTPCodes
                          ]
                        }
                      />
                    </div>
                  </div>
                </StatusAlert>
              </div>
            </Card.Body>
          </Card>
        </div>

        <div className="section">
          <Card className={styles.card}>
            <Card.Header>
              <Title level={2} content={i18n.t("title.headers")} />
            </Card.Header>
            <Card.Body className="cardBody">
              <div className="spaceBetweenContainerStart">
                <Table striped bordered hover>
                  <thead>
                    <tr className={styles.headerTableContainer}>
                      <th className={styles.tableTitle} colSpan={2}>
                        <Title level={3} content={i18n.t("title.request")} />
                      </th>
                    </tr>
                    <tr>
                      <th>{i18n.t("label.name")}</th>
                      <th>{i18n.t("label.value")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {partHeaderRequest.map((p, index) => (
                      <tr key={index}>
                        <td>{p.name}</td>
                        <td>{p.valueString}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                <Table striped bordered hover className={styles.table}>
                  <thead>
                    <tr className={styles.headerTableContainer}>
                      <th className={styles.tableTitle} colSpan={2}>
                        <Title level={3} content={i18n.t("title.response")} />
                      </th>
                    </tr>
                    <tr>
                      <th>{i18n.t("label.name")}</th>
                      <th>{i18n.t("label.value")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {partHeaderResponse.map((p, index) => (
                      <tr key={index}>
                        <td>{p.name}</td>
                        <td>{p.valueString}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </div>

        <div className="section">
          <Card>
            <Card.Header>
              <Title level={2} content={i18n.t("title.body")} />
            </Card.Header>
            <Card.Body>
              <Card>
                <Card.Header>
                  <Title level={3} content={i18n.t("title.request")} />
                </Card.Header>
                {bodyRequest === "N/A" && <Card.Body>{bodyRequest}</Card.Body>}
                {bodyRequest !== "N/A" && (
                  <Card.Body>
                    <ReactJson src={parseJson(bodyRequest)} name={false} />
                  </Card.Body>
                )}
              </Card>

              <div className={styles.responseSection}>
                <Card className={styles.responseCard}>
                  <Card.Header>
                    <Title level={3} content={i18n.t("title.response")} />
                  </Card.Header>
                  {bodyResponse === "N/A" && (
                    <Card.Body>{bodyResponse}</Card.Body>
                  )}
                  {bodyResponse !== "N/A" && (
                    <Card.Body>
                      <ReactJson src={parseJson(bodyResponse)} name={false} />
                    </Card.Body>
                  )}
                </Card>
              </div>
            </Card.Body>
          </Card>
        </div>
      </>
    </PandoraPage>
  );
};

export default AuditEventExchangeDetails;
